import { PageProps } from '../../shell/shell';
import {
  useGetPublisherTripsByTripIdStatusQuery,
  useGetStoriesByTripByTripIdQuery,
  useGetTripsByTripIdQuery,
  usePostPublisherTripsByTripIdPublishMutation,
  usePostStoriesByTripByTripIdStubMutation,
} from '../../store/backendApi';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { StoryBox } from './story/storyBox';
import { EditTripHeader } from './editTripHeader';
import { DividerWithButton } from '../../components/dividers/dividerWithButton';
import { PlusIcon } from '@heroicons/react/24/outline';
import { UpdateTripModal } from './settingsModal/updateTripModal';
import { BackButton } from '../../components/backButton';

export const EditTripPage = (props: PageProps) => {
  const params = useParams();
  const tripId = params['tripId']!;
  const [editingStoryId, setEditingStoryId] = useState<string | null>(null);
  const nav = useNavigate();

  const { data: tripData, error: tripDataError } = useGetTripsByTripIdQuery(tripId);
  const { data: storiesData, error: storiesError } = useGetStoriesByTripByTripIdQuery(tripId);

  const [publishChanges, { error: publishError, isLoading: publishLoading }] =
    usePostPublisherTripsByTripIdPublishMutation();

  const [hasChanges, setHasChanges] = useState(false);

  const [editHeader, setEditHeader] = useState(false);

  const [createStory, { isLoading: isCreatingStory }] = usePostStoriesByTripByTripIdStubMutation();
  const { data: publishState, error: publishStateError } =
    useGetPublisherTripsByTripIdStatusQuery(tripId);

  useEffect(() => {
    if (!publishState) {
      return;
    }
    setHasChanges(publishState.hasUnpublishedChanges);
  }, [publishState]);

  const addStory = async () => {
    await createStory(tripId)
      .unwrap()
      .then((story) => {
        setEditingStoryId(story.id);
      });
  };

  const publish = async () => {
    await publishChanges(tripId);
    if (publishError) {
      alert('could not publish changes');
    } else {
      setHasChanges(false);
    }
  };

  if (tripDataError) {
    return <p>could not load trip data</p>;
  }
  if (storiesError) {
    return <p>could not load stories data</p>;
  }
  if (publishStateError) {
    return <p>could not load publish state data</p>;
  }

  return (
    <div className="relative">
      <BackButton />
      <EditTripHeader
        tripData={tripData}
        username={props.user?.username}
        userId={props.user?.userId}
      />

      {/* Overlay when editing */}
      {editingStoryId && (
        <div className="fixed inset-0 bg-black bg-opacity-20 pointer-events-none z-80" />
      )}

      <div className="flex w-full justify-center mt-3">
        <div className="bg-black/50 backdrop-blur-sm rounded-lg px-4 py-3 shadow-lg">
          <div className="flex gap-2">
            <button
              className={`rounded-md px-2 py-1 text-sm font-semibold bg-white shadow-sm focus-visible:outline 
                focus-visible:outline-2 focus-visible:outline-offset-2 text-gray-900`}
              onClick={() => {
                setEditHeader(true);
              }}
            >
              Settings
            </button>
            {tripData && tripData.shareMode !== 'PRIVATE' && (
              <button
                disabled={!hasChanges || publishLoading}
                className={`rounded-md px-2 py-1 text-sm font-semibold text-white shadow-sm focus-visible:outline 
                focus-visible:outline-2 focus-visible:outline-offset-2 bg-indigo-600 hover:bg-indigo-500 
                focus-visible:outline-indigo-600 disabled:bg-indigo-200 disabled:hover:cursor-not-allowed`}
                title={
                  hasChanges ? (publishLoading ? 'Publishing...' : 'Publish') : 'Already up-to-date'
                }
                onClick={() => {
                  publish();
                }}
              >
                {publishLoading ? 'Publishing...' : 'Publish'}
              </button>
            )}
            {tripData && tripData.shareMode == 'PRIVATE' && (
              <button
                disabled={!hasChanges || publishLoading}
                className={`rounded-md px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm focus-visible:outline 
                focus-visible:outline-2 focus-visible:outline-offset-2 bg-white`}
                title="View"
                onClick={() => {
                  nav('/p/trip/' + tripData.id);
                }}
              >
                View
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-16 w-[90%] mx-auto">
        <DividerWithButton
          text={'Add Story'}
          onClick={() => {
            setHasChanges(true);
            addStory();
          }}
          inlineElement={
            <PlusIcon aria-hidden="true" className="-ml-1 -mr-0.5 size-5 text-gray-400" />
          }
        />
        {storiesData?.stories &&
          [...storiesData.stories]
            .sort((a, b) => {
              const dateA = new Date(a.date).getTime();
              const dateB = new Date(b.date).getTime();
              return dateB - dateA;
            })
            .map((story) => (
              <StoryBox
                key={story.id}
                story={story}
                editingStoryId={editingStoryId}
                setEditingStoryId={setEditingStoryId}
                setHasChanges={setHasChanges}
              />
            ))}
      </div>
      <footer>
        <div className="mx-auto max-w-7xl px-6 py-12 md:flex md:items-center md:justify-between lg:px-8">
          <p className="mt-8 text-center text-sm/6 text-gray-600 md:order-1 md:mt-0">
            All rights reserved.
          </p>
        </div>
      </footer>
      {tripData && props.user?.userId && (
        <UpdateTripModal
          open={editHeader}
          setOpen={setEditHeader}
          tripData={tripData}
          userId={props.user.userId}
          username={props.user.username}
          setHasChanges={setHasChanges}
        />
      )}
    </div>
  );
};
